/* eslint-disable @angular-eslint/directive-selector */

import { isPlatformBrowser } from '@angular/common'
import {
    Directive,
    EmbeddedViewRef,
    Inject,
    Input,
    PLATFORM_ID,
    TemplateRef,
    ViewContainerRef,
    ɵstringify as stringify,
} from '@angular/core'

@Directive({
    selector: '[ifSsr]',
})
export class IfSsrDirective {
    private context: IfSsrContext = new IfSsrContext()
    private ifTemplateRef: TemplateRef<IfSsrContext> | null = null
    private ifViewRef: EmbeddedViewRef<IfSsrContext> | null = null
    private readonly elseTemplateRef: TemplateRef<IfSsrContext> | null = null
    private elseViewRef: EmbeddedViewRef<IfSsrContext> | null = null

    constructor(
        private viewContainer: ViewContainerRef,
        templateRef: TemplateRef<IfSsrContext>,
        @Inject(PLATFORM_ID) private platformId: string,
    ) {
        this.elseTemplateRef = templateRef
    }

    /**
     * The Template to be shown if the element is being rendered on the server-side.
     */
    @Input()
    set ifSsr(templateRef: TemplateRef<IfSsrContext> | null) {
        if (templateRef) {
            IfSsrDirective.isTemplate('ifSsr', templateRef)
            this.ifTemplateRef = templateRef
        }
        this.ifViewRef = null
        this.updateView()
    }

    private updateView(): void {
        this.context.isSsr = isPlatformBrowser(this.platformId)
        if (! this.context.isSsr) {
            if (! this.ifViewRef) {
                this.viewContainer.clear()
                this.elseViewRef = null
                if (this.ifTemplateRef) {
                    this.ifViewRef =
                        this.viewContainer.createEmbeddedView(this.ifTemplateRef, this.context)
                }
            }
        } else {
            if (! this.elseViewRef) {
                this.viewContainer.clear()
                this.ifViewRef = null
                if (this.elseTemplateRef) {
                    this.elseViewRef =
                        this.viewContainer.createEmbeddedView(this.elseTemplateRef, this.context)
                }

            }
        }
    }

    /** @internal */

    /**
     * Assert the correct type of the expression bound to the `ifSsr` input within the template.
     *
     * The presence of this static field is a signal to the Ivy template type check compiler that
     * when the `IfSsr` structural directive renders its template, the type of the expression bound
     * to `ifSsr` should be narrowed in some way. For `IfSsr`, the binding expression itself is used to
     * narrow its type, which allows the strictNullChecks feature of TypeScript to work with `IfSsr`.
     */
    static ngTemplateGuard_ifSsr: 'binding'

    /**
     * Asserts the correct type of the context for the template that `IfSsr` will render.
     *
     * The presence of this method is a signal to the Ivy template type-check compiler that the
     * `IfSsr` structural directive renders its template with a specific context type.
     */
    static ngTemplateContextGuard(dir: IfSsrDirective, ctx: any): ctx is IfSsrContext {
        return ctx.isSsr !== undefined
    }

    public static isTemplate(property: string, templateRef: TemplateRef<any> | null): boolean {
        const isTemplateRefOrNull = !! (! templateRef || templateRef.createEmbeddedView)
        if (! isTemplateRefOrNull) {
            throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`)
        }
        return isTemplateRefOrNull
    }
}

export class IfSsrContext {
    public isSsr: boolean
}
